/**
 * @fileoverview Data Collection Zustand store
 *
 * Manages client-side state for templates, collection records,
 * stats, filters, and promotion suggestions.
 *
 * @author FacilityAI
 * @version 1.0.0
 */

import { create } from "zustand";
import type {
	CollectionTemplate,
	CollectionRecord,
	CollectionStats,
} from "@/services/data-collection-service";

export interface DCFilters {
	status?: string;
	templateId?: number;
	locationId?: number;
	search?: string;
}

interface DataCollectionState {
	// Templates
	templates: CollectionTemplate[];
	templatesLoaded: boolean;
	setTemplates: (templates: CollectionTemplate[]) => void;

	// Records
	records: CollectionRecord[];
	total: number;
	setRecords: (records: CollectionRecord[], total: number) => void;

	// Filters
	filters: DCFilters;
	setFilters: (filters: DCFilters) => void;
	resetFilters: () => void;

	// Loading
	loading: boolean;
	setLoading: (loading: boolean) => void;

	// Selected record
	selectedRecord: CollectionRecord | null;
	detailLoading: boolean;
	setSelectedRecord: (record: CollectionRecord | null) => void;
	setDetailLoading: (loading: boolean) => void;

	// Stats
	stats: CollectionStats | null;
	statsLoading: boolean;
	setStats: (stats: CollectionStats | null) => void;
	setStatsLoading: (loading: boolean) => void;

	// Promotion suggestions
	promotionSuggestions: CollectionRecord[];
	setPromotionSuggestions: (suggestions: CollectionRecord[]) => void;
}

const defaultFilters: DCFilters = {};

export const useDataCollectionStore = create<DataCollectionState>((set) => ({
	// Templates
	templates: [],
	templatesLoaded: false,
	setTemplates: (templates) => set({ templates, templatesLoaded: true }),

	// Records
	records: [],
	total: 0,
	setRecords: (records, total) => set({ records, total }),

	// Filters
	filters: { ...defaultFilters },
	setFilters: (filters) => set({ filters }),
	resetFilters: () => set({ filters: { ...defaultFilters } }),

	// Loading
	loading: false,
	setLoading: (loading) => set({ loading }),

	// Selected record
	selectedRecord: null,
	detailLoading: false,
	setSelectedRecord: (selectedRecord) => set({ selectedRecord }),
	setDetailLoading: (detailLoading) => set({ detailLoading }),

	// Stats
	stats: null,
	statsLoading: false,
	setStats: (stats) => set({ stats }),
	setStatsLoading: (statsLoading) => set({ statsLoading }),

	// Promotion suggestions
	promotionSuggestions: [],
	setPromotionSuggestions: (promotionSuggestions) => set({ promotionSuggestions }),
}));
